import { bffCrmPersonTimelineSummary } from '@fireflyhealth/api'

import { useQuery } from '~/shared/hooks/query'
import type { JourneySummary } from './MemberTimelineService'

/** Statuses a job will not move on from, so polling stops. */
const TERMINAL_STATUSES: JourneySummary['status'][] = ['complete', 'skipped', 'failed']

const isTerminal = (status: JourneySummary['status'] | undefined) =>
  status != null && TERMINAL_STATUSES.includes(status)

const POLL_INTERVAL_MILLIS = 2000

const createJourneySummaryKey = (personId: number) => ['createJourneySummary', personId]

const pollJourneySummaryKey = (personId: number, summaryId: number | null) => [
  'pollJourneySummary',
  personId,
  summaryId,
]

/**
 * The member's journey summary, asking for a fresh one only when the feed has moved past the
 * one already served. `enabled` must include "the feed has loaded": before it has, `fromFeed`
 * is undefined, which is indistinguishable from a member who has no summary.
 */
export const useJourneySummary = (variables: {
  personId: number | undefined
  fromFeed: JourneySummary | null | undefined
  enabled: boolean
}) => {
  const { personId, fromFeed, enabled } = variables
  const wanted = enabled && personId != null && (fromFeed == null || fromFeed.stale)

  const created = useQuery<JourneySummary>(
    createJourneySummaryKey(Number(personId)),
    async () => (await bffCrmPersonTimelineSummary.create({ person_id: Number(personId) })).data,
    { enabled: wanted, retry: false },
  )

  const active = created.data ?? fromFeed ?? null
  const pendingId = active != null && !isTerminal(active.status) ? active.id : null

  const polled = useQuery<JourneySummary>(
    pollJourneySummaryKey(Number(personId), pendingId),
    async () =>
      (
        await bffCrmPersonTimelineSummary.retrieve({
          person_id: Number(personId),
          summary_id: Number(pendingId),
        })
      ).data,
    {
      enabled: enabled && pendingId != null,
      retry: false,
      refetchInterval: query =>
        isTerminal(query.state.data?.status) ? false : POLL_INTERVAL_MILLIS,
    },
  )

  // Newest by id, not newest-fetched: disabling a query does not clear its cache. Ordered so
  // a poll beats the create it polled, which share an id.
  const candidates = [fromFeed, created.data, polled.data].filter(
    (candidate): candidate is JourneySummary => candidate != null,
  )
  const latest = newest(candidates)
  // A failed run must not take away the answer the reader already had: its own row carries no
  // text, while the feed still serves the last one that worked.
  const succeeded = newest(candidates.filter(candidate => candidate.status === 'complete'))

  return {
    summary: succeeded ?? latest,
    isGenerating: latest != null && !isTerminal(latest.status),
    hasFailed: latest?.status === 'failed' || created.error != null || polled.error != null,
  }
}

const newest = (summaries: JourneySummary[]): JourneySummary | null =>
  summaries.reduce<JourneySummary | null>(
    (best, candidate) => (best == null || candidate.id >= best.id ? candidate : best),
    null,
  )
